import { isEmpty } from 'mojito/utils';
import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import BonusCard from 'modules/bonuses/bonus-card/index.js';
import BonusesLoadingIndicator from 'modules/bonuses/bonuses-loading-indicator/index.js';
import { useMemo } from 'react';

const { Text, ProgressBar, FlexPane } = MojitoPresentation.Components;
const CurrencyHelper = MojitoPresentation.Utils.CurrencyHelper;
const NumberUtils = MojitoCore.Base.NumberUtils;
const { BONUS_TYPE } = MojitoServices.Bonus.types;

const ActiveBonuses = props => {
    const {
        oddsBoosts,
        campaigns,
        currencyCode,
        showPrewager,
        showOddsBoosts,
        isLoading,
        mojitoTools: { config, stringResolver },
    } = props;

    const freeBets = useMemo(
        () =>
            props.freeBets.map(freeBet => ({
                ...freeBet,
                bonusType: BONUS_TYPE.FREEBET,
            })),
        [props.freeBets]
    );

    function renderBonusItemsNotAvailable(items, infoString) {
        if (!isEmpty(items)) {
            return null;
        }

        return (
            <FlexPane config={config.noBonusesCard}>
                <Text class="ActiveBonuses-NoBonus" config={config.bonusItemsNotAvailable}>
                    {infoString}
                </Text>
            </FlexPane>
        );
    }

    function renderProgressContent(target, wagered, remaining, currencyCode) {
        const progressRatio = NumberUtils.numberToPrecision(wagered / target, 2);
        const remainingRatio = 1 - progressRatio;
        const remainingLabel =
            remainingRatio > config.remainingProgressVisibilityLimit / 100 &&
            CurrencyHelper.formatCurrency(remaining, currencyCode);

        return (
            <ProgressBar
                config={config.progressBar}
                progress={progressRatio}
                endLabel={CurrencyHelper.formatCurrency(target, currencyCode)}
                progressLabel={CurrencyHelper.formatCurrency(wagered, currencyCode)}
                remainLabel={remainingLabel}
                isActiveBonus={true}
            />
        );
    }

    return isLoading ? (
        <BonusesLoadingIndicator
            config={config.loadingIndicator}
            labelClassName="ta-fetchingActiveBonusesText"
        />
    ) : (
        <FlexPane config={config.container} class="ta-ActiveBonuses">
            <FlexPane config={config.header} class="ta-bonusesList-freeBets">
                <FlexPane config={config.headerTextWrapper}>
                    <Text config={config.headerText} class="ta-FreeBetHeaderText">
                        {stringResolver.resolveString(`$ACTIVE_BONUSES.FREE_BET`)}
                    </Text>
                </FlexPane>
                {freeBets.map(bonus => (
                    <BonusCard
                        config={config.bonusCard}
                        shouldShowRemainingAmount={config.shouldShowRemainingAmount}
                        key={bonus.id}
                        bonus={bonus}
                        currencyCode={currencyCode}
                    />
                ))}
                {renderBonusItemsNotAvailable(
                    freeBets,
                    stringResolver.resolveString(`$ACTIVE_BONUSES.NO_FREE_BETS`)
                )}
            </FlexPane>
            {showOddsBoosts && (
                <FlexPane config={config.header} class="ta-bonusesList-oddsBoost">
                    <FlexPane config={config.headerTextWrapper}>
                        <Text config={config.headerText} class="ta-OddsBoostHeaderText">
                            {stringResolver.resolveString(`$ACTIVE_BONUSES.ODDS_BOOST`)}
                        </Text>
                    </FlexPane>
                    {oddsBoosts.map(bonus => (
                        <BonusCard
                            config={config.bonusCard}
                            shouldShowRemainingAmount={config.shouldShowRemainingAmount}
                            key={bonus.id}
                            bonus={bonus}
                            currencyCode={currencyCode}
                        />
                    ))}
                    {renderBonusItemsNotAvailable(
                        oddsBoosts,
                        stringResolver.resolveString(`$ACTIVE_BONUSES.NO_ODDS_BOOSTS`)
                    )}
                </FlexPane>
            )}
            {showPrewager && (
                <FlexPane config={config.header} class="ta-bonusesList-prewager">
                    <FlexPane config={config.headerTextWrapper}>
                        <Text config={config.headerText} class="ta-PrewagerHeaderText">
                            {stringResolver.resolveString(`$ACTIVE_BONUSES.PREWAGER`)}
                        </Text>
                    </FlexPane>
                    {campaigns.map(bonus => (
                        <BonusCard
                            config={config.bonusCard}
                            shouldShowRemainingAmount={config.shouldShowRemainingAmount}
                            key={bonus.id}
                            bonus={bonus}
                            currencyCode={currencyCode}
                            renderProgressContent={renderProgressContent}
                        />
                    ))}

                    {renderBonusItemsNotAvailable(
                        campaigns,
                        stringResolver.resolveString(`$ACTIVE_BONUSES.NO_PREWAGER`)
                    )}
                </FlexPane>
            )}
        </FlexPane>
    );
};

export default ActiveBonuses;
